import { intersection } from 'lodash';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useAllCostCenters } from '~/data/costCenters';
import { EmployeeCostCenter } from '~/data/employee';
import { getTranslationKeyForObjectKey } from '~/features/ScheduledEmploymentChanges/helpers';
import {
  EmploymentUpcomingChangeDifference,
  UpcomingChangeValueFormatter,
  useResolveUpcomingChanges,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/Common/EmploymentUpcomingChanges';
import {
  DataPairingProcessPayload,
  PairedDataItem,
  useDataPairingForComparison,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/data/useDataPairingForComparison';

enum ObjectKeyTranslation_CostCenters {
  'fEmpId' = 'fEmpId',
  'fEmpCostCenter' = 'laCostCenter',
  'fEmpCostCenterIsDefault' = 'laDefault',
  'fEmpCostCenterPercent' = 'laPercCostCenter',
}

const EmpPositionKeys_CostCenters = [
  'fEmpId',
  'fEmpCostCenter',
  'fEmpCostCenterIsDefault',
  'fEmpCostCenterPercent',
];

export const useUpcomingCostCenterChanges = () => {
  const { t } = useTranslation();
  const { resolveUpcomingChanges, isLoading: isLoadingUpcomingResolvers } =
    useResolveUpcomingChanges();
  const { resolveDataItemPair, resolveRestDataItemPairs } =
    useDataPairingForComparison();

  const { costCenters, isLoading: isLoadingCostCenters } = useAllCostCenters();
  const getCostCenterName = useCallback(
    (ccId: number): string => {
      if (ccId < 0) {
        return '';
      }
      return (
        costCenters.find((cc) => cc.fCostCenterId === ccId)?.fCostCenterName ||
        ccId.toString()
      );
    },
    [costCenters],
  );

  const translateFieldName_CostCenter = useCallback(
    (key: string): string => {
      const translationKey = getTranslationKeyForObjectKey<
        typeof ObjectKeyTranslation_CostCenters
      >(key, ObjectKeyTranslation_CostCenters);

      return t(translationKey);
    },
    [t],
  );

  const formatFieldValue_costCenter: UpcomingChangeValueFormatter = useCallback(
    (id: string, value?: string | number | boolean): string => {
      if (typeof value === 'undefined') {
        return '';
      }

      switch (id) {
        case 'fEmpCostCenter':
          return typeof value === 'number' ? getCostCenterName(value) : '';
        case 'fEmpCostCenterIsDefault':
          return !!value ? 'Default' : '';
        case 'fEmpCostCenterPercent':
          return `${!!value ? value : 0}%`;
        default: {
          return `${value}`;
        }
      }
    },
    [getCostCenterName],
  );

  const resolveCostCenterPairing = useCallback(
    (oldArray: EmployeeCostCenter[], newArray: EmployeeCostCenter[]) => {
      const emptyCostCenter: EmployeeCostCenter = {
        fEmpCostCenter: -1,
        fEmpCostCenterCustomData: {},
        fEmpCostCenterIsDefault: false,
        fEmpId: -1,
        fEmpCostCenterPercent: 0,
      };

      const costCenterPairing = (
        props: DataPairingProcessPayload<EmployeeCostCenter>,
      ): PairedDataItem<EmployeeCostCenter>[] => {
        const resolveDefaultPair = (
          p: DataPairingProcessPayload<EmployeeCostCenter>,
        ): DataPairingProcessPayload<EmployeeCostCenter> => {
          const getDefaultCCIndex = (ccArray: EmployeeCostCenter[]) =>
            ccArray.findIndex((cc) => cc.fEmpCostCenterIsDefault);

          const { payload } = resolveDataItemPair(
            p,
            getDefaultCCIndex,
            emptyCostCenter,
          );

          return payload;
        };

        const resolveIdPairs = (
          p: DataPairingProcessPayload<EmployeeCostCenter>,
        ): DataPairingProcessPayload<EmployeeCostCenter> => {
          const getCCIds = (array: EmployeeCostCenter[]) => {
            return array.map((cc) => cc.fEmpCostCenter);
          };

          const oldIds = getCCIds(p.unprocessed.oldArray);
          const newIds = getCCIds(p.unprocessed.newArray);

          // Process items with common id's
          return intersection(oldIds, newIds).reduce(
            (
              lastPayload: DataPairingProcessPayload<EmployeeCostCenter>,
              ccId,
            ) => {
              const getIndexById = (ccArray: EmployeeCostCenter[]) =>
                ccArray.findIndex((cc) => cc.fEmpCostCenter === ccId);

              const { payload } = resolveDataItemPair(
                lastPayload,
                getIndexById,
                emptyCostCenter,
              );

              return payload;
            },
            p,
          );
        };

        const { paired } = resolveRestDataItemPairs(
          resolveIdPairs(resolveDefaultPair(props)),
          emptyCostCenter,
        );

        return paired;
      };

      return costCenterPairing({
        unprocessed: {
          oldArray,
          newArray,
        },
        paired: [],
        checkList: {},
      });
    },
    [resolveDataItemPair, resolveRestDataItemPairs],
  );

  const resolveUpcomingCostCenterChanges = useCallback(
    (
      oldData: EmployeeCostCenter[],
      newData: EmployeeCostCenter[],
    ): EmploymentUpcomingChangeDifference[] => {
      return resolveCostCenterPairing(oldData, newData)
        .map((ccData) => {
          return resolveUpcomingChanges({
            standardFields: {
              fieldKeys: EmpPositionKeys_CostCenters,
              fieldKeyExceptions: ['fEmpId'],
              ...ccData,
              valueFormatter: formatFieldValue_costCenter,
              labelTranslator: translateFieldName_CostCenter,
            },
            customFields: {
              customEnumGroupKey: 'CUSTOM_ENTITY_COST_CENTER',
              customDataEntity: 'COST_CENTER',
              oldData: ccData.oldData.fEmpCostCenterCustomData,
              newData: ccData.newData.fEmpCostCenterCustomData,
            },
          }).map(
            (diff): EmploymentUpcomingChangeDifference => ({
              groupLabel: `${t('fEmpCostCenter')} (${getCostCenterName(
                ccData.newData.fEmpCostCenter > 0
                  ? ccData.newData.fEmpCostCenter
                  : ccData.oldData.fEmpCostCenter,
              )})`,
              ...diff,
            }),
          );
        })
        .flat();
    },
    [
      t,
      getCostCenterName,
      resolveCostCenterPairing,
      resolveUpcomingChanges,
      formatFieldValue_costCenter,
      translateFieldName_CostCenter,
    ],
  );

  return {
    resolveUpcomingCostCenterChanges,
    isLoading: isLoadingCostCenters || isLoadingUpcomingResolvers,
  };
};
